/**
 * Employee Detail Page - HR view of individual employee's complete information
 */

import { useState, useEffect } from 'react'
import { MaskedField } from '../components/MaskedField'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { employeeInfoApi } from '../api/employeeInfoApi'
import { useAuth } from '@/core/auth/AuthContext'
import LoadingPage from '@/shared/components/LoadingPage'
import toast from 'react-hot-toast'
import type {
  ProfileInfoInput,
  DocumentSummary,
  EmergencyContactSummary,
  EmergencyContactInput,
  FamilyMemberSummary,
  FamilyMemberInput,
  AddressSummary,
  AddressInput,
  BankAccountSummary,
  BankAccountInput,
} from '../types/employee-info.types'
import {
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_CATEGORY_LABELS,
  RELATIONSHIP_LABELS,
  FAMILY_RELATIONSHIP_LABELS,
  ADDRESS_TYPE_LABELS,
  MARITAL_STATUS_LABELS,
  EDUCATION_LEVEL_LABELS,
  GENDER_LABELS,
  ACCOUNT_TYPE_LABELS,
} from '../types/employee-info.types'

type ActiveSection = 'profile' | 'documents' | 'emergency' | 'family' | 'addresses' | 'bank'
type ModalType = 'profile' | 'emergency' | 'family' | 'address' | 'bank' | null

const EmployeeDetailPage = () => {
  const { employeeId } = useParams<{ employeeId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [activeSection, setActiveSection] = useState<ActiveSection>('profile')
  const [editModal, setEditModal] = useState<{ type: ModalType; id?: string } | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: string; id: string } | null>(null)

  const isAdminOrHR =
    user?.roles?.some((r) => r.name === 'admin' || r.name === 'hr') ||
    user?.division_name?.toLowerCase() === 'human resources'

  const canEdit =
    user?.roles?.some((r) => r.name === 'hr') ||
    user?.division_name?.toLowerCase() === 'human resources'

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['employeeProfile', employeeId],
    queryFn: () => employeeInfoApi.getEmployeeProfile(employeeId!),
    enabled: !!employeeId && isAdminOrHR,
  })

  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['employeeDocuments', employeeId],
    queryFn: () => employeeInfoApi.getEmployeeDocuments(employeeId!),
    enabled: !!employeeId && isAdminOrHR,
  })

  const { data: contactsData, isLoading: contactsLoading } = useQuery({
    queryKey: ['employeeEmergencyContacts', employeeId],
    queryFn: () => employeeInfoApi.getEmployeeEmergencyContacts(employeeId!),
    enabled: !!employeeId && isAdminOrHR,
  })

  const { data: familyData, isLoading: familyLoading } = useQuery({
    queryKey: ['employeeFamilyMembers', employeeId],
    queryFn: () => employeeInfoApi.getEmployeeFamilyMembers(employeeId!),
    enabled: !!employeeId && isAdminOrHR,
  })

  const { data: addressesData, isLoading: addressesLoading } = useQuery({
    queryKey: ['employeeAddresses', employeeId],
    queryFn: () => employeeInfoApi.getEmployeeAddresses(employeeId!),
    enabled: !!employeeId && isAdminOrHR,
  })

  const { data: bankData, isLoading: bankLoading } = useQuery({
    queryKey: ['employeeBankAccounts', employeeId],
    queryFn: () => employeeInfoApi.getEmployeeBankAccounts(employeeId!),
    enabled: !!employeeId && isAdminOrHR,
  })

  // Fetch full records for edit modals
  const { data: editingContact } = useQuery({
    queryKey: ['emergencyContact', editModal?.id],
    queryFn: () => employeeInfoApi.getEmergencyContact(editModal!.id!),
    enabled: editModal?.type === 'emergency' && !!editModal?.id,
  })

  const { data: editingMember } = useQuery({
    queryKey: ['familyMember', editModal?.id],
    queryFn: () => employeeInfoApi.getFamilyMember(editModal!.id!),
    enabled: editModal?.type === 'family' && !!editModal?.id,
  })

  const { data: editingAddress } = useQuery({
    queryKey: ['address', editModal?.id],
    queryFn: () => employeeInfoApi.getAddress(editModal!.id!),
    enabled: editModal?.type === 'address' && !!editModal?.id,
  })

  const { data: editingBankAccount } = useQuery({
    queryKey: ['bankAccount', editModal?.id],
    queryFn: () => employeeInfoApi.getBankAccount(editModal!.id!),
    enabled: editModal?.type === 'bank' && !!editModal?.id,
  })

  // Form state
  const [profileForm, setProfileForm] = useState<ProfileInfoInput>({})
  const [contactForm, setContactForm] = useState<Partial<EmergencyContactInput>>({})
  const [familyForm, setFamilyForm] = useState<Partial<FamilyMemberInput>>({})
  const [addressForm, setAddressForm] = useState<Partial<AddressInput>>({})
  const [bankForm, setBankForm] = useState<Partial<BankAccountInput>>({})

  // Initialize edit forms when data loads
  useEffect(() => {
    if (editModal?.type === 'profile' && profile) {
      setProfileForm({
        nickname: profile.nickname ?? undefined,
        gender: profile.gender ?? undefined,
        date_of_birth: profile.date_of_birth ?? undefined,
        place_of_birth: profile.place_of_birth ?? undefined,
        nationality: profile.nationality ?? undefined,
        religion: profile.religion ?? undefined,
        marital_status: profile.marital_status ?? undefined,
        blood_type: profile.blood_type ?? undefined,
        ktp_number: profile.ktp_number ?? undefined,
        npwp_number: profile.npwp_number ?? undefined,
        bpjs_health_number: profile.bpjs_health_number ?? undefined,
        bpjs_employment_number: profile.bpjs_employment_number ?? undefined,
        passport_number: profile.passport_number ?? undefined,
        passport_expiry: profile.passport_expiry ?? undefined,
        highest_education: profile.highest_education ?? undefined,
        education_institution: profile.education_institution ?? undefined,
        education_major: profile.education_major ?? undefined,
        graduation_year: profile.graduation_year ?? undefined,
      })
    }
  }, [editModal?.type, profile])

  useEffect(() => {
    if (editingContact) {
      setContactForm({
        contact_name: editingContact.contact_name,
        relationship_type: editingContact.relationship_type,
        phone_primary: editingContact.phone_primary,
        phone_secondary: editingContact.phone_secondary ?? undefined,
        email: editingContact.email ?? undefined,
        address: editingContact.address ?? undefined,
        is_primary: editingContact.is_primary,
      })
    }
  }, [editingContact])

  useEffect(() => {
    if (editingMember) {
      setFamilyForm({
        full_name: editingMember.full_name,
        relationship_type: editingMember.relationship_type,
        gender: editingMember.gender ?? undefined,
        date_of_birth: editingMember.date_of_birth ?? undefined,
        place_of_birth: editingMember.place_of_birth ?? undefined,
        occupation: editingMember.occupation ?? undefined,
        id_number: editingMember.id_number ?? undefined,
        is_dependent: editingMember.is_dependent,
        is_beneficiary: editingMember.is_beneficiary,
      })
    }
  }, [editingMember])

  useEffect(() => {
    if (editingAddress) {
      setAddressForm({
        address_type: editingAddress.address_type,
        street_address: editingAddress.street_address,
        rt_rw: editingAddress.rt_rw ?? undefined,
        kelurahan: editingAddress.kelurahan ?? undefined,
        kecamatan: editingAddress.kecamatan ?? undefined,
        city: editingAddress.city,
        province: editingAddress.province,
        postal_code: editingAddress.postal_code,
        country: editingAddress.country,
        is_primary: editingAddress.is_primary,
      })
    }
  }, [editingAddress])

  useEffect(() => {
    if (editingBankAccount) {
      setBankForm({
        bank_name: editingBankAccount.bank_name,
        bank_code: editingBankAccount.bank_code ?? undefined,
        branch_name: editingBankAccount.branch_name ?? undefined,
        account_number: editingBankAccount.account_number,
        account_holder_name: editingBankAccount.account_holder_name,
        account_type: editingBankAccount.account_type,
        is_primary: editingBankAccount.is_primary,
      })
    }
  }, [editingBankAccount])

  // Mutations
  const updateProfileMutation = useMutation({
    mutationFn: (data: ProfileInfoInput) =>
      employeeInfoApi.updateEmployeeProfile(employeeId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeProfile', employeeId] })
      setEditModal(null)
      toast.success('Profile updated')
    },
    onError: () => toast.error('Failed to update profile'),
  })

  const saveContactMutation = useMutation({
    mutationFn: (data: EmergencyContactInput) =>
      editModal?.id
        ? employeeInfoApi.updateEmergencyContact(editModal.id, data)
        : employeeInfoApi.createEmployeeEmergencyContact(employeeId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeEmergencyContacts', employeeId] })
      setEditModal(null)
      toast.success(editModal?.id ? 'Contact updated' : 'Contact added')
    },
    onError: () => toast.error('Failed to save contact'),
  })

  const deleteContactMutation = useMutation({
    mutationFn: (id: string) => employeeInfoApi.deleteEmergencyContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeEmergencyContacts', employeeId] })
      setDeleteConfirm(null)
      toast.success('Contact deleted')
    },
    onError: () => toast.error('Failed to delete contact'),
  })

  const saveMemberMutation = useMutation({
    mutationFn: (data: FamilyMemberInput) =>
      editModal?.id
        ? employeeInfoApi.updateFamilyMember(editModal.id, data)
        : employeeInfoApi.createEmployeeFamilyMember(employeeId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeFamilyMembers', employeeId] })
      setEditModal(null)
      toast.success(editModal?.id ? 'Member updated' : 'Member added')
    },
    onError: () => toast.error('Failed to save family member'),
  })

  const deleteMemberMutation = useMutation({
    mutationFn: (id: string) => employeeInfoApi.deleteFamilyMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeFamilyMembers', employeeId] })
      setDeleteConfirm(null)
      toast.success('Member deleted')
    },
    onError: () => toast.error('Failed to delete member'),
  })

  const saveAddressMutation = useMutation({
    mutationFn: (data: AddressInput) =>
      editModal?.id
        ? employeeInfoApi.updateAddress(editModal.id, data)
        : employeeInfoApi.createEmployeeAddress(employeeId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeAddresses', employeeId] })
      setEditModal(null)
      toast.success(editModal?.id ? 'Address updated' : 'Address added')
    },
    onError: () => toast.error('Failed to save address'),
  })

  const deleteAddressMutation = useMutation({
    mutationFn: (id: string) => employeeInfoApi.deleteAddress(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeAddresses', employeeId] })
      setDeleteConfirm(null)
      toast.success('Address deleted')
    },
    onError: () => toast.error('Failed to delete address'),
  })

  const saveBankMutation = useMutation({
    mutationFn: (data: BankAccountInput) =>
      editModal?.id
        ? employeeInfoApi.updateBankAccount(editModal.id, data)
        : employeeInfoApi.createEmployeeBankAccount(employeeId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeBankAccounts', employeeId] })
      setEditModal(null)
      toast.success(editModal?.id ? 'Account updated' : 'Account added')
    },
    onError: () => toast.error('Failed to save bank account'),
  })

  const deleteBankMutation = useMutation({
    mutationFn: (id: string) => employeeInfoApi.deleteBankAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeBankAccounts', employeeId] })
      setDeleteConfirm(null)
      toast.success('Account deleted')
    },
    onError: () => toast.error('Failed to delete account'),
  })

  const handleDownload = async (documentId: string, filename: string) => {
    try {
      const blob = await employeeInfoApi.downloadDocument(documentId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      console.error('Download failed')
    }
  }

  const openAddModal = (type: ModalType) => {
    if (type === 'emergency') setContactForm({})
    if (type === 'family') setFamilyForm({})
    if (type === 'address') setAddressForm({ country: 'Indonesia' })
    if (type === 'bank') setBankForm({ account_type: 'savings' })
    setEditModal({ type })
  }

  const openEditModal = (type: ModalType, id: string) => {
    setEditModal({ type, id })
  }

  if (!isAdminOrHR) {
    navigate('/employee-info')
    return null
  }

  if (profileLoading) return <LoadingPage />

  const sections = [
    {
      id: 'profile',
      label: 'Profile',
      icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    },
    {
      id: 'emergency',
      label: 'Emergency',
      icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
    },
    {
      id: 'family',
      label: 'Family',
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    },
    {
      id: 'addresses',
      label: 'Addresses',
      icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
    },
    {
      id: 'bank',
      label: 'Bank',
      icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    },
  ]

  const documents = documentsData?.data || []
  const contacts = contactsData?.data || []
  const family = familyData?.data || []
  const addresses = addressesData?.data || []
  const bankAccounts = bankData?.data || []

  const isEditLoading =
    (editModal?.type === 'emergency' && !!editModal.id && !editingContact) ||
    (editModal?.type === 'family' && !!editModal.id && !editingMember) ||
    (editModal?.type === 'address' && !!editModal.id && !editingAddress) ||
    (editModal?.type === 'bank' && !!editModal.id && !editingBankAccount)

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 px-6 py-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-indigo-200 text-sm mb-4">
            <Link to="/employee-info" className="hover:text-white transition-colors">
              Employee Info
            </Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link
              to="/employee-info/directory"
              className="hover:text-white transition-colors"
            >
              Directory
            </Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">Employee Details</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold">
              {profile?.employee?.full_name?.charAt(0) ||
                profile?.nickname?.charAt(0) ||
                'E'}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">
                {profile?.employee?.full_name || profile?.nickname || 'Employee'}
              </h1>
              {profile?.employee && (
                <p className="text-indigo-200 text-sm mt-1">
                  {profile.employee.job_title && (
                    <span>{profile.employee.job_title}</span>
                  )}
                  {profile.employee.job_title && profile.employee.department && (
                    <span> • </span>
                  )}
                  {profile.employee.department && (
                    <span>{profile.employee.department}</span>
                  )}
                  {(profile.employee.job_title || profile.employee.department) && (
                    <span> • </span>
                  )}
                  <span>{profile.employee.email}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <div className="w-64 flex-shrink-0">
            <nav className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 sticky top-8">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id as ActiveSection)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                    activeSection === section.id
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={section.icon}
                    />
                  </svg>
                  <span className="font-medium">{section.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Profile Section */}
            {activeSection === 'profile' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
                  {canEdit && <button
                    onClick={() => openEditModal('profile', employeeId!)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit Profile
                  </button>}
                </div>
                {profile ? (
                  <div className="grid grid-cols-2 gap-6">
                    <InfoField label="Nickname" value={profile.nickname} />
                    <InfoField
                      label="Gender"
                      value={
                        profile.gender ? GENDER_LABELS[profile.gender] : undefined
                      }
                    />
                    <InfoField label="Date of Birth" value={profile.date_of_birth} />
                    <InfoField label="Place of Birth" value={profile.place_of_birth} />
                    <InfoField label="Nationality" value={profile.nationality} />
                    <InfoField label="Religion" value={profile.religion} />
                    <InfoField
                      label="Marital Status"
                      value={
                        profile.marital_status
                          ? MARITAL_STATUS_LABELS[profile.marital_status]
                          : undefined
                      }
                    />
                    <InfoField label="Blood Type" value={profile.blood_type} />
                    <MaskedField label="KTP Number" value={profile.ktp_number} />
                    <MaskedField label="NPWP Number" value={profile.npwp_number} />
                    <MaskedField label="BPJS Health" value={profile.bpjs_health_number} />
                    <MaskedField
                      label="BPJS Employment"
                      value={profile.bpjs_employment_number}
                    />
                    <MaskedField label="Passport Number" value={profile.passport_number} />
                    <InfoField label="Passport Expiry" value={profile.passport_expiry} />
                    <InfoField
                      label="Education"
                      value={
                        profile.highest_education
                          ? EDUCATION_LEVEL_LABELS[profile.highest_education]
                          : undefined
                      }
                    />
                    <InfoField
                      label="Institution"
                      value={profile.education_institution}
                    />
                    <InfoField label="Major" value={profile.education_major} />
                    <InfoField
                      label="Graduation Year"
                      value={profile.graduation_year?.toString()}
                    />
                  </div>
                ) : (
                  <p className="text-gray-500">No profile information available</p>
                )}
              </div>
            )}

            {/* Documents Section */}
            {activeSection === 'documents' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Documents</h2>
                {documentsLoading ? (
                  <p className="text-gray-500">Loading...</p>
                ) : documents.length === 0 ? (
                  <p className="text-gray-500">No documents uploaded</p>
                ) : (
                  <div className="space-y-4">
                    {documents.map((doc: DocumentSummary) => (
                      <div
                        key={doc.id}
                        className="border border-gray-200 rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                              <svg
                                className="w-6 h-6 text-indigo-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{doc.title}</h3>
                              <p className="text-sm text-gray-500">
                                {DOCUMENT_TYPE_LABELS[doc.document_type]} •{' '}
                                {DOCUMENT_CATEGORY_LABELS[doc.document_category]}
                              </p>
                              {doc.is_expired && (
                                <p className="text-sm text-red-600">Expired</p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownload(doc.id, doc.original_filename)}
                            className="p-2 text-gray-500 hover:text-indigo-600 transition-colors"
                            title="Download"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Emergency Contacts Section */}
            {activeSection === 'emergency' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Emergency Contacts</h2>
                  {canEdit && <button
                    onClick={() => openAddModal('emergency')}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Contact
                  </button>}
                </div>
                {contactsLoading ? (
                  <p className="text-gray-500">Loading...</p>
                ) : contacts.length === 0 ? (
                  <p className="text-gray-500">No emergency contacts added</p>
                ) : (
                  <div className="space-y-4">
                    {contacts.map((contact: EmergencyContactSummary) => (
                      <div
                        key={contact.id}
                        className="border border-gray-200 rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-gray-900">
                                {contact.contact_name}
                              </h3>
                              {contact.is_primary && (
                                <span className="px-2 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700">
                                  Primary
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">
                              {RELATIONSHIP_LABELS[contact.relationship_type]}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {contact.phone_primary}
                            </p>
                          </div>
                          {canEdit && <div className="flex items-center gap-2">
                            <button
                              onClick={() => openEditModal('emergency', contact.id)}
                              className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                              title="Edit"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() =>
                                setDeleteConfirm({ type: 'emergency', id: contact.id })
                              }
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Family Members Section */}
            {activeSection === 'family' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Family Members</h2>
                  {canEdit && <button
                    onClick={() => openAddModal('family')}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Member
                  </button>}
                </div>
                {familyLoading ? (
                  <p className="text-gray-500">Loading...</p>
                ) : family.length === 0 ? (
                  <p className="text-gray-500">No family members added</p>
                ) : (
                  <div className="space-y-4">
                    {family.map((member: FamilyMemberSummary) => (
                      <div
                        key={member.id}
                        className="border border-gray-200 rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium text-gray-900">{member.full_name}</h3>
                            <p className="text-sm text-gray-500">
                              {FAMILY_RELATIONSHIP_LABELS[member.relationship_type]}
                            </p>
                            <div className="flex gap-2 mt-2">
                              {member.is_dependent && (
                                <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                                  Dependent
                                </span>
                              )}
                              {member.is_beneficiary && (
                                <span className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700">
                                  Beneficiary
                                </span>
                              )}
                            </div>
                          </div>
                          {canEdit && <div className="flex items-center gap-2">
                            <button
                              onClick={() => openEditModal('family', member.id)}
                              className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                              title="Edit"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() =>
                                setDeleteConfirm({ type: 'family', id: member.id })
                              }
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Addresses Section */}
            {activeSection === 'addresses' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Addresses</h2>
                  {canEdit && <button
                    onClick={() => openAddModal('address')}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Address
                  </button>}
                </div>
                {addressesLoading ? (
                  <p className="text-gray-500">Loading...</p>
                ) : addresses.length === 0 ? (
                  <p className="text-gray-500">No addresses added</p>
                ) : (
                  <div className="space-y-4">
                    {addresses.map((addr: AddressSummary) => (
                      <div
                        key={addr.id}
                        className="border border-gray-200 rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-gray-900">
                                {ADDRESS_TYPE_LABELS[addr.address_type]}
                              </h3>
                              {addr.is_primary && (
                                <span className="px-2 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700">
                                  Primary
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {addr.city}, {addr.province}
                            </p>
                          </div>
                          {canEdit && <div className="flex items-center gap-2">
                            <button
                              onClick={() => openEditModal('address', addr.id)}
                              className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                              title="Edit"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() =>
                                setDeleteConfirm({ type: 'address', id: addr.id })
                              }
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bank Accounts Section */}
            {activeSection === 'bank' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Bank Accounts</h2>
                  {canEdit && <button
                    onClick={() => openAddModal('bank')}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Account
                  </button>}
                </div>
                {bankLoading ? (
                  <p className="text-gray-500">Loading...</p>
                ) : bankAccounts.length === 0 ? (
                  <p className="text-gray-500">No bank accounts added</p>
                ) : (
                  <div className="space-y-4">
                    {bankAccounts.map((account: BankAccountSummary) => (
                      <div
                        key={account.id}
                        className="border border-gray-200 rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-medium text-gray-900">
                                {account.bank_name}
                              </h3>
                              {account.is_primary && (
                                <span className="px-2 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700">
                                  Primary
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">
                              {account.account_holder_name}
                            </p>
                            <p className="text-sm text-gray-500 mb-2">
                              {ACCOUNT_TYPE_LABELS[account.account_type]}
                            </p>
                            <MaskedField
                              label="Account Number"
                              value={account.account_number}
                            />
                          </div>
                          {canEdit && <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => openEditModal('bank', account.id)}
                              className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                              title="Edit"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() =>
                                setDeleteConfirm({ type: 'bank', id: account.id })
                              }
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modals */}
      {editModal && canEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {isEditLoading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : (
              <>
                {/* Profile Edit Form */}
                {editModal.type === 'profile' && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      updateProfileMutation.mutate(profileForm)
                    }}
                  >
                    <div className="p-6 border-b border-gray-100">
                      <h3 className="text-lg font-semibold text-gray-900">Edit Profile</h3>
                    </div>
                    <div className="p-6 grid grid-cols-2 gap-4">
                      <FormField label="Nickname">
                        <input
                          type="text"
                          value={profileForm.nickname || ''}
                          onChange={(e) =>
                            setProfileForm({ ...profileForm, nickname: e.target.value || undefined })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Gender">
                        <select
                          value={profileForm.gender || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              gender: (e.target.value as 'male' | 'female') || undefined,
                            })
                          }
                          className={inputClass}
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      </FormField>
                      <FormField label="Date of Birth">
                        <input
                          type="date"
                          value={profileForm.date_of_birth || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              date_of_birth: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Place of Birth">
                        <input
                          type="text"
                          value={profileForm.place_of_birth || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              place_of_birth: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Nationality">
                        <input
                          type="text"
                          value={profileForm.nationality || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              nationality: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Religion">
                        <input
                          type="text"
                          value={profileForm.religion || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              religion: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Marital Status">
                        <select
                          value={profileForm.marital_status || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              marital_status:
                                (e.target.value as
                                  | 'single'
                                  | 'married'
                                  | 'divorced'
                                  | 'widowed') || undefined,
                            })
                          }
                          className={inputClass}
                        >
                          <option value="">Select status</option>
                          {Object.entries(MARITAL_STATUS_LABELS).map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Blood Type">
                        <input
                          type="text"
                          value={profileForm.blood_type || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              blood_type: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                          placeholder="A / B / AB / O"
                        />
                      </FormField>
                      <FormField label="KTP Number">
                        <input
                          type="text"
                          value={profileForm.ktp_number || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              ktp_number: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="NPWP Number">
                        <input
                          type="text"
                          value={profileForm.npwp_number || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              npwp_number: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="BPJS Health Number">
                        <input
                          type="text"
                          value={profileForm.bpjs_health_number || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              bpjs_health_number: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="BPJS Employment Number">
                        <input
                          type="text"
                          value={profileForm.bpjs_employment_number || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              bpjs_employment_number: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Passport Number">
                        <input
                          type="text"
                          value={profileForm.passport_number || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              passport_number: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Passport Expiry">
                        <input
                          type="date"
                          value={profileForm.passport_expiry || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              passport_expiry: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Highest Education">
                        <select
                          value={profileForm.highest_education || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              highest_education:
                                (e.target.value as
                                  | 'sd'
                                  | 'smp'
                                  | 'sma'
                                  | 'd1'
                                  | 'd2'
                                  | 'd3'
                                  | 's1'
                                  | 's2'
                                  | 's3') || undefined,
                            })
                          }
                          className={inputClass}
                        >
                          <option value="">Select education</option>
                          {Object.entries(EDUCATION_LEVEL_LABELS).map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Institution">
                        <input
                          type="text"
                          value={profileForm.education_institution || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              education_institution: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Major">
                        <input
                          type="text"
                          value={profileForm.education_major || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              education_major: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Graduation Year">
                        <input
                          type="number"
                          value={profileForm.graduation_year || ''}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              graduation_year: e.target.value
                                ? parseInt(e.target.value)
                                : undefined,
                            })
                          }
                          className={inputClass}
                          placeholder="e.g. 2020"
                        />
                      </FormField>
                    </div>
                    <ModalFooter
                      onCancel={() => setEditModal(null)}
                      isPending={updateProfileMutation.isPending}
                    />
                  </form>
                )}

                {/* Emergency Contact Form */}
                {editModal.type === 'emergency' && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      saveContactMutation.mutate(contactForm as EmergencyContactInput)
                    }}
                  >
                    <div className="p-6 border-b border-gray-100">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {editModal.id ? 'Edit Contact' : 'Add Emergency Contact'}
                      </h3>
                    </div>
                    <div className="p-6 grid grid-cols-2 gap-4">
                      <FormField label="Name" required className="col-span-2">
                        <input
                          type="text"
                          value={contactForm.contact_name || ''}
                          onChange={(e) =>
                            setContactForm({ ...contactForm, contact_name: e.target.value })
                          }
                          required
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Relationship" required>
                        <select
                          value={contactForm.relationship_type || ''}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              relationship_type: e.target.value as EmergencyContactInput['relationship_type'],
                            })
                          }
                          required
                          className={inputClass}
                        >
                          <option value="">Select relationship</option>
                          {Object.entries(RELATIONSHIP_LABELS).map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Primary Phone" required>
                        <input
                          type="text"
                          value={contactForm.phone_primary || ''}
                          onChange={(e) =>
                            setContactForm({ ...contactForm, phone_primary: e.target.value })
                          }
                          required
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Secondary Phone">
                        <input
                          type="text"
                          value={contactForm.phone_secondary || ''}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              phone_secondary: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Email">
                        <input
                          type="email"
                          value={contactForm.email || ''}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              email: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Address" className="col-span-2">
                        <input
                          type="text"
                          value={contactForm.address || ''}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              address: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <div className="col-span-2 flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="contact-primary"
                          checked={contactForm.is_primary || false}
                          onChange={(e) =>
                            setContactForm({ ...contactForm, is_primary: e.target.checked })
                          }
                          className="rounded border-gray-300 text-indigo-600"
                        />
                        <label
                          htmlFor="contact-primary"
                          className="text-sm text-gray-700"
                        >
                          Primary emergency contact
                        </label>
                      </div>
                    </div>
                    <ModalFooter
                      onCancel={() => setEditModal(null)}
                      isPending={saveContactMutation.isPending}
                    />
                  </form>
                )}

                {/* Family Member Form */}
                {editModal.type === 'family' && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      saveMemberMutation.mutate(familyForm as FamilyMemberInput)
                    }}
                  >
                    <div className="p-6 border-b border-gray-100">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {editModal.id ? 'Edit Family Member' : 'Add Family Member'}
                      </h3>
                    </div>
                    <div className="p-6 grid grid-cols-2 gap-4">
                      <FormField label="Full Name" required className="col-span-2">
                        <input
                          type="text"
                          value={familyForm.full_name || ''}
                          onChange={(e) =>
                            setFamilyForm({ ...familyForm, full_name: e.target.value })
                          }
                          required
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Relationship" required>
                        <select
                          value={familyForm.relationship_type || ''}
                          onChange={(e) =>
                            setFamilyForm({
                              ...familyForm,
                              relationship_type: e.target.value as FamilyMemberInput['relationship_type'],
                            })
                          }
                          required
                          className={inputClass}
                        >
                          <option value="">Select relationship</option>
                          {Object.entries(FAMILY_RELATIONSHIP_LABELS).map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Gender">
                        <select
                          value={familyForm.gender || ''}
                          onChange={(e) =>
                            setFamilyForm({
                              ...familyForm,
                              gender: (e.target.value as 'male' | 'female') || undefined,
                            })
                          }
                          className={inputClass}
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      </FormField>
                      <FormField label="Date of Birth">
                        <input
                          type="date"
                          value={familyForm.date_of_birth || ''}
                          onChange={(e) =>
                            setFamilyForm({
                              ...familyForm,
                              date_of_birth: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Place of Birth">
                        <input
                          type="text"
                          value={familyForm.place_of_birth || ''}
                          onChange={(e) =>
                            setFamilyForm({
                              ...familyForm,
                              place_of_birth: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Occupation">
                        <input
                          type="text"
                          value={familyForm.occupation || ''}
                          onChange={(e) =>
                            setFamilyForm({
                              ...familyForm,
                              occupation: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="ID Number">
                        <input
                          type="text"
                          value={familyForm.id_number || ''}
                          onChange={(e) =>
                            setFamilyForm({
                              ...familyForm,
                              id_number: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <div className="col-span-2 flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="member-dependent"
                            checked={familyForm.is_dependent || false}
                            onChange={(e) =>
                              setFamilyForm({
                                ...familyForm,
                                is_dependent: e.target.checked,
                              })
                            }
                            className="rounded border-gray-300 text-indigo-600"
                          />
                          <label
                            htmlFor="member-dependent"
                            className="text-sm text-gray-700"
                          >
                            Dependent
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="member-beneficiary"
                            checked={familyForm.is_beneficiary || false}
                            onChange={(e) =>
                              setFamilyForm({
                                ...familyForm,
                                is_beneficiary: e.target.checked,
                              })
                            }
                            className="rounded border-gray-300 text-indigo-600"
                          />
                          <label
                            htmlFor="member-beneficiary"
                            className="text-sm text-gray-700"
                          >
                            Beneficiary
                          </label>
                        </div>
                      </div>
                    </div>
                    <ModalFooter
                      onCancel={() => setEditModal(null)}
                      isPending={saveMemberMutation.isPending}
                    />
                  </form>
                )}

                {/* Address Form */}
                {editModal.type === 'address' && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      saveAddressMutation.mutate(addressForm as AddressInput)
                    }}
                  >
                    <div className="p-6 border-b border-gray-100">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {editModal.id ? 'Edit Address' : 'Add Address'}
                      </h3>
                    </div>
                    <div className="p-6 grid grid-cols-2 gap-4">
                      <FormField label="Address Type" required>
                        <select
                          value={addressForm.address_type || ''}
                          onChange={(e) =>
                            setAddressForm({
                              ...addressForm,
                              address_type: e.target.value as AddressInput['address_type'],
                            })
                          }
                          required
                          className={inputClass}
                        >
                          <option value="">Select type</option>
                          {Object.entries(ADDRESS_TYPE_LABELS).map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="RT/RW">
                        <input
                          type="text"
                          value={addressForm.rt_rw || ''}
                          onChange={(e) =>
                            setAddressForm({
                              ...addressForm,
                              rt_rw: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                          placeholder="e.g. 001/002"
                        />
                      </FormField>
                      <FormField label="Street Address" required className="col-span-2">
                        <input
                          type="text"
                          value={addressForm.street_address || ''}
                          onChange={(e) =>
                            setAddressForm({
                              ...addressForm,
                              street_address: e.target.value,
                            })
                          }
                          required
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Kelurahan">
                        <input
                          type="text"
                          value={addressForm.kelurahan || ''}
                          onChange={(e) =>
                            setAddressForm({
                              ...addressForm,
                              kelurahan: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Kecamatan">
                        <input
                          type="text"
                          value={addressForm.kecamatan || ''}
                          onChange={(e) =>
                            setAddressForm({
                              ...addressForm,
                              kecamatan: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="City" required>
                        <input
                          type="text"
                          value={addressForm.city || ''}
                          onChange={(e) =>
                            setAddressForm({ ...addressForm, city: e.target.value })
                          }
                          required
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Province" required>
                        <input
                          type="text"
                          value={addressForm.province || ''}
                          onChange={(e) =>
                            setAddressForm({ ...addressForm, province: e.target.value })
                          }
                          required
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Postal Code" required>
                        <input
                          type="text"
                          value={addressForm.postal_code || ''}
                          onChange={(e) =>
                            setAddressForm({
                              ...addressForm,
                              postal_code: e.target.value,
                            })
                          }
                          required
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Country">
                        <input
                          type="text"
                          value={addressForm.country || 'Indonesia'}
                          onChange={(e) =>
                            setAddressForm({
                              ...addressForm,
                              country: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <div className="col-span-2 flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="addr-primary"
                          checked={addressForm.is_primary || false}
                          onChange={(e) =>
                            setAddressForm({
                              ...addressForm,
                              is_primary: e.target.checked,
                            })
                          }
                          className="rounded border-gray-300 text-indigo-600"
                        />
                        <label htmlFor="addr-primary" className="text-sm text-gray-700">
                          Primary address
                        </label>
                      </div>
                    </div>
                    <ModalFooter
                      onCancel={() => setEditModal(null)}
                      isPending={saveAddressMutation.isPending}
                    />
                  </form>
                )}

                {/* Bank Account Form */}
                {editModal.type === 'bank' && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      saveBankMutation.mutate(bankForm as BankAccountInput)
                    }}
                  >
                    <div className="p-6 border-b border-gray-100">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {editModal.id ? 'Edit Bank Account' : 'Add Bank Account'}
                      </h3>
                    </div>
                    <div className="p-6 grid grid-cols-2 gap-4">
                      <FormField label="Bank Name" required>
                        <input
                          type="text"
                          value={bankForm.bank_name || ''}
                          onChange={(e) =>
                            setBankForm({ ...bankForm, bank_name: e.target.value })
                          }
                          required
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Bank Code">
                        <input
                          type="text"
                          value={bankForm.bank_code || ''}
                          onChange={(e) =>
                            setBankForm({
                              ...bankForm,
                              bank_code: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Branch Name">
                        <input
                          type="text"
                          value={bankForm.branch_name || ''}
                          onChange={(e) =>
                            setBankForm({
                              ...bankForm,
                              branch_name: e.target.value || undefined,
                            })
                          }
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Account Type">
                        <select
                          value={bankForm.account_type || 'savings'}
                          onChange={(e) =>
                            setBankForm({
                              ...bankForm,
                              account_type: e.target.value as BankAccountInput['account_type'],
                            })
                          }
                          className={inputClass}
                        >
                          {Object.entries(ACCOUNT_TYPE_LABELS).map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Account Number" required className="col-span-2">
                        <input
                          type="text"
                          value={bankForm.account_number || ''}
                          onChange={(e) =>
                            setBankForm({ ...bankForm, account_number: e.target.value })
                          }
                          required
                          className={inputClass}
                        />
                      </FormField>
                      <FormField
                        label="Account Holder Name"
                        required
                        className="col-span-2"
                      >
                        <input
                          type="text"
                          value={bankForm.account_holder_name || ''}
                          onChange={(e) =>
                            setBankForm({
                              ...bankForm,
                              account_holder_name: e.target.value,
                            })
                          }
                          required
                          className={inputClass}
                        />
                      </FormField>
                      <div className="col-span-2 flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="bank-primary"
                          checked={bankForm.is_primary || false}
                          onChange={(e) =>
                            setBankForm({ ...bankForm, is_primary: e.target.checked })
                          }
                          className="rounded border-gray-300 text-indigo-600"
                        />
                        <label htmlFor="bank-primary" className="text-sm text-gray-700">
                          Primary bank account
                        </label>
                      </div>
                    </div>
                    <ModalFooter
                      onCancel={() => setEditModal(null)}
                      isPending={saveBankMutation.isPending}
                    />
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && canEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this{' '}
              {deleteConfirm.type === 'emergency'
                ? 'emergency contact'
                : deleteConfirm.type === 'family'
                  ? 'family member'
                  : deleteConfirm.type === 'address'
                    ? 'address'
                    : 'bank account'}
              ? This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteConfirm.type === 'emergency')
                    deleteContactMutation.mutate(deleteConfirm.id)
                  else if (deleteConfirm.type === 'family')
                    deleteMemberMutation.mutate(deleteConfirm.id)
                  else if (deleteConfirm.type === 'address')
                    deleteAddressMutation.mutate(deleteConfirm.id)
                  else if (deleteConfirm.type === 'bank')
                    deleteBankMutation.mutate(deleteConfirm.id)
                }}
                disabled={
                  deleteContactMutation.isPending ||
                  deleteMemberMutation.isPending ||
                  deleteAddressMutation.isPending ||
                  deleteBankMutation.isPending
                }
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper components
const InfoField = ({ label, value }: { label: string; value?: string | null }) => (
  <div>
    <p className="text-sm text-gray-500">{label}</p>
    <p className="font-medium text-gray-900">{value || '-'}</p>
  </div>
)

const FormField = ({
  label,
  required,
  children,
  className = '',
}: {
  label: string
  required?: boolean
  children: React.ReactNode
  className?: string
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
  </div>
)

const ModalFooter = ({
  onCancel,
  isPending,
}: {
  onCancel: () => void
  isPending: boolean
}) => (
  <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
    <button
      type="button"
      onClick={onCancel}
      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
    >
      Cancel
    </button>
    <button
      type="submit"
      disabled={isPending}
      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50"
    >
      {isPending ? 'Saving...' : 'Save'}
    </button>
  </div>
)

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none'

export default EmployeeDetailPage
